import React from "react";
import styled from "@emotion/styled";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  IconButton as MuiIconButton,
  Stack,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";

import { ReactComponent as StatusIcon } from "../../../vendor/waf/green_circle.svg";


import useAuth from "../../../hooks/useAuth";
import useLLMConfig from "../../../hooks/user/useLLMConfig";
import { LLMOutputScannersData, UserType } from "../../../utils/constants";
import { SnackbarAlert, IOSSwitch, Paper } from "../../../components/pages/common/styled";
import LLMConfigHeader from "./llmHeader";
import { formatDate } from "../../../utils/format";
import EditOutputScannerModal from "../../../components/pages/llm/guard/output/M_EditOutputScanner";

const IconButton = styled(MuiIconButton)`
  padding: 4px;
`;
const headCells = [
  { id: "no", alignment: "left", label: "No" },
  { id: "name", alignment: "left", label: "Name" },
  { id: "enabled", alignment: "left", label: "Enabled" },
  { id: "action", alignment: "left", label: "More" },
];


const EnhancedTableHead = () => {
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            sx={{ padding: "8px" }}
            key={headCell.id}
            align={headCell.alignment}
            padding={headCell.disablePadding ? "none" : "normal"}
          >
            <Typography variant="tableHeader">{headCell.label}</Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};
function SkeletonContent(props) {
  const { rowsPerPage } = props;
  var rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }
  var cellArray = [1, 2, 3, 4, 5];

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell sx={{ padding: "16px 8px" }} key={"s_" + r + "_" + c}>
              <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}
SkeletonContent.propTypes = { rowsPerPage: PropTypes.number.isRequired };

function LLMOutputScanners() {
  const { configZone } = useParams();
  const zoneUid = configZone;

  const { isAuthenticated, zoneController, homeController, wafdashController, webzoneController, wafeventController, planController, userType } = useAuth();
  const { llmOutputScanners, getLLMOutputScanners, configLLMSettingOutputScanners, errMsg, setErr } = useLLMConfig();

  const [rows, setRows] = React.useState(null);
  const [currentKey, setCurrentKey] = React.useState(null);

  const [viewOpen, setViewOpen] = React.useState(false);
  const viewHandleOpen = () => setViewOpen(true);
  const viewHandleClose = () => setViewOpen(false);

  const handleClickMore = (key) => {
    if (userType === UserType.ORG_USER) return;
    setCurrentKey(key);
    viewHandleOpen();
  };

  React.useEffect(() => {
    if (isAuthenticated) {
      if (zoneController) zoneController.abort();
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (webzoneController) webzoneController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
      if (zoneUid) {
        getLLMOutputScanners(zoneUid);
      }
    }
    setErr(null);
  }, [isAuthenticated, getLLMOutputScanners, zoneUid, setErr]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (llmOutputScanners) {
      setRows(Object.values(LLMOutputScannersData).map((value) => {
        return {
          id: value.ID,
          key: value.KEY,
          name: value.NAME,
          enabled: llmOutputScanners?.[value.KEY]?.enabled
        };
      }));
    }
  }, [llmOutputScanners]);

  const changePageEnable = (event, id) => {
    if (userType === UserType.ORG_USER) return;
    configLLMSettingOutputScanners(zoneUid, rows[event.target.name].key, {enabled: !rows[event.target.name].enabled});
  };
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const handleSnackClose = () => {
    setSnackOpen(false);
    setErr(null);
  };

  React.useEffect(() => {
    if (errMsg) {
      setSnackOpen(true);
      setMessage(errMsg);
      setSuccess("error");
    } else {
      setSnackOpen(false);
      setMessage(null);
    }
  }, [errMsg]);

  return (
    <React.Fragment>
      <LLMConfigHeader title={"LLM Output Scanners"} url={"config/output_scanners"} />
      <Box sx={{ background: "white", borderRadius: "28px 28px 28px 28px", padding: "36px 24px" }}>
        <Grid container px={4} pb={6}>
          <Grid item>
            <Typography variant="h2" display="inline">
              LLM Output Scanners
            </Typography>
          </Grid>
        </Grid>
        <Paper sx={{ maxWidth: "90vw", boxShadow: "none" }}>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size={"medium"} aria-label="enhanced table">
              <EnhancedTableHead />
              {rows === null ? (
                <SkeletonContent rowsPerPage={3} />
              ) : (
                <TableBody>
                  {rows?.map((row, index) => {
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={`${row?.id}-${index}`}
                      >
                        <TableCell align="left" sx={{ padding: "8px" }}>
                          {row?.id}
                        </TableCell>
                        <TableCell align="left" sx={{ padding: "8px" }}>
                          {row?.name}
                        </TableCell>
                        <TableCell align="left" sx={{ padding: "8px" }}>
                          <IOSSwitch
                            color="green"
                            checked={row?.enabled}
                            name={index}
                            onChange={(event) => changePageEnable(event, row?.id)}
                          />
                        </TableCell>
                        <TableCell align="left" sx={{ padding: "8px" }}>
                          <IconButton
                            size="large"
                            aria-label="details"
                            id="demo-customized-button"
                            onClick={() => handleClickMore(row?.key)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <Stack direction="row" alignItems="center" bgcolor="white" px={4} pt={8}>
            <StatusIcon />
            <Typography ml={2}>Last updated {formatDate(llmOutputScanners?.updated_at)}</Typography>
          </Stack>
          <EditOutputScannerModal open={viewOpen} handleClose={viewHandleClose} zoneUid={zoneUid} currentKey={currentKey} />
        </Paper>
      </Box>
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default LLMOutputScanners;
